/**
 * ES2022/ES2023 methods missing in the older Safari and Chrome versions some customers still use (Sentry LUKA-WEB-C6,
 * LUKA-WEB-BK). Our code and our dependencies call them, and Vite doesn't polyfill built-ins.
 */
type Predicate<T> = (value: T, index: number, list: ArrayLike<T>) => unknown

export function at<T>(list: ArrayLike<T>, index: number): T | undefined {
	const relative = Math.trunc(index) || 0
	const actual = relative < 0 ? list.length + relative : relative

	return actual < 0 || actual >= list.length ? undefined : list[actual]
}

export function findLastIndex<T>(
	list: ArrayLike<T>,
	predicate: Predicate<T>,
	thisArg?: unknown
): number {
	for (let index = list.length - 1; index >= 0; index--) {
		if (predicate.call(thisArg, list[index]!, index, list)) return index
	}

	return -1
}

export function findLast<T>(
	list: ArrayLike<T>,
	predicate: Predicate<T>,
	thisArg?: unknown
): T | undefined {
	const index = findLastIndex(list, predicate, thisArg)

	return index === -1 ? undefined : list[index]
}

function define(target: object, name: string, value: unknown): void {
	if (name in target) return

	Object.defineProperty(target, name, { value, writable: true, configurable: true })
}

define(Array.prototype, 'at', function (this: unknown[], index: number) {
	return at(this, index)
})
define(String.prototype, 'at', function (this: string, index: number) {
	return at(String(this), index)
})
define(
	Array.prototype,
	'findLast',
	function (this: unknown[], predicate: Predicate<unknown>, thisArg?: unknown) {
		return findLast(this, predicate, thisArg)
	}
)
define(
	Array.prototype,
	'findLastIndex',
	function (this: unknown[], predicate: Predicate<unknown>, thisArg?: unknown) {
		return findLastIndex(this, predicate, thisArg)
	}
)
